<template>
  <component is="style" type="text/css">
    {{ style }}
  </component>
  <div
    class="section__divider"
    :style="dividerStyles"
    :class="{
      'section__divider--flip': settings?.flip,
      'section__divider--to-front': settings?.front
    }"
  >
    <component :is="DIVIDERS[settings?.type]" v-if="settings?.type" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import CurveAsymmetrical from '../../assets/svg/curve-asymmetrical-negative.svg';
import Curve from '../../assets/svg/curve-negative.svg';
import Tilt from '../../assets/svg/tilt.svg';
import TiltOpacity from '../../assets/svg/opacity-tilt.svg';
import Clouds from '../../assets/svg/clouds.svg';
import Triangle from '../../assets/svg/triangle.svg';
import TriangleAsymmetrical from '../../assets/svg/triangle-asymmetrical-negative.svg';
import Waves from '../../assets/svg/waves-negative.svg';
import WavesPattern from '../../assets/svg/waves-pattern.svg';
import WavesBrush from '../../assets/svg/wave-brush.svg';
import Zigzag from '../../assets/svg/zigzag.svg';
import Pyramids from '../../assets/svg/pyramids.svg';
import Drops from '../../assets/svg/drops.svg';
import Split from '../../assets/svg/split-negative.svg';
import Mountains from '../../assets/svg/mountains.svg';
import FanOpacity from '../../assets/svg/opacity-fan.svg';
import Book from '../../assets/svg/book-negative.svg';
import { DividerState } from '@/components/layout/SettingsModel';
import { DividerPositionType } from '@/typings/enums/enums';

const DIVIDERS = {
  curve_asymmetrical: CurveAsymmetrical,
  curve: Curve,
  tilt: Tilt,
  tilt_opacity: TiltOpacity,
  clouds: Clouds,
  triangle: Triangle,
  triangle_asymmetrical: TriangleAsymmetrical,
  waves: Waves,
  waves_pattern: WavesPattern,
  waves_brush: WavesBrush,
  zigzag: Zigzag,
  pyramids: Pyramids,
  drops: Drops,
  split: Split,
  mountains: Mountains,
  fan_opacity: FanOpacity,
  book: Book
};

interface DividerStyles {
  width: string;
  height: string;
  color?: string;
  top?: string;
  bottom?: string;
}

export default defineComponent({
  name: 'UIDivider',
  props: {
    settings: {
      type: Object as PropType<DividerState>
    },
    sectionId: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const style = computed(() => {
      let invert = '';
      let rotate = '';
      if (props.settings && props.settings.invert) {
        invert += ' rotateX(180deg) ';
      }
      if (props.settings && props.settings.flip) {
        rotate += ' rotateY(180deg) ';
      }
      const scalaX =
        (props.settings && props.settings.width ? 'scaleX(' + props.settings.width / 100 + ')' : '') + invert + rotate;

      let style = '';
      style +=
        '#prefix# svg { transform: ' +
        scalaX +
        '!important; -webkiz-transform: ' +
        scalaX +
        '!important; -moz-transform:' +
        scalaX +
        '!important; }';

      if (props.settings?.position) {
        switch (props.settings?.position) {
          case DividerPositionType.TOP:
            style += '#prefix# { top: 0px !important; bottom: auto !important; }';

            break;

          case DividerPositionType.BOTTOM:
            style += '#prefix# { top: auto !important; bottom: 0px !important; }';

            break;
          default:
            style += '#prefix# { top: 0px !important; bottom: auto !important; }';
        }
      }

      const prefix = `.site .section.section--${props.sectionId} .section__divider`;

      return style.replaceAll('#prefix#', prefix);
    });

    const dividerStyles = computed(() => {
      let style: DividerStyles = {
        height: Number(props.settings?.height) + 'px',
        width: '100%',
        color: props.settings?.color
      };

      return style;
    });

    return {
      dividerStyles,
      style,
      DIVIDERS
    };
  }
});
</script>

<style lang="scss">
.section__divider {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 20%;
  pointer-events: none;
  overflow: hidden;

  &--flip {
    svg {
      transform: rotate(180deg);
    }
  }
  &--to-front {
    z-index: 4;
  }

  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform-origin: 50% 50%;
    fill: currentColor;
  }

  path {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform-origin: 50% 50%;
    fill: currentColor;
  }
}
</style>
